import { useProductionOrderIndicators } from "@/features/production-orders/hooks";
import { Notify } from "@/components/ui/notify";
import type { RunKpis } from "@/types/sim-sandbox.types";
import { composeSimVsReal, type SimVsRealRow } from "../sim-vs-real.helpers";
import { DeltaValue, SectionLabel } from "./result-primitives";

interface Props {
  productionOrderId: string;
  sim: RunKpis;
}

const fmt = (v: number | null, unit: "pct" | "count"): string => {
  if (v == null) return "—";
  return unit === "pct" ? `${v.toFixed(1)}%` : v.toLocaleString("pt-BR");
};

// Backtest de OP executada: a rodada DETALHADA (DES real) × registro real
// da OP é o DESVIO OFICIAL por indicador (o preview leve é só estimativa). Distinção explícita
// SIM/REAL/DESVIO; lacunas mostram "—" (nunca inventa valor).
export function SimVsRealSection({ productionOrderId, sim }: Props) {
  const indicatorsQuery = useProductionOrderIndicators(productionOrderId);
  const { data, isLoading, isError, error, refetch } = indicatorsQuery;
  const exec = data?.execution ?? null;

  if (isError) {
    return <Notify.QueryError error={error} onRetry={() => void refetch()} variant="inline" />;
  }

  if (isLoading) {
    return <p className="text-xs text-muted-foreground">Carregando indicadores reais da OP…</p>;
  }

  const rows: SimVsRealRow[] = composeSimVsReal(sim, {
    oee: exec?.oee ?? null,
    availability: exec?.availability ?? null,
    performance: exec?.performance ?? null,
    quality: exec?.quality ?? null,
    producedQty: exec?.producedQty ?? null,
  });

  return (
    <div>
      <SectionLabel>Simulado × real · desvio oficial</SectionLabel>
      {!exec ? (
        <p className="text-xs text-muted-foreground">
          A OP ainda não tem indicadores reais registrados — sem comparação disponível.
        </p>
      ) : (
        <div className="overflow-hidden rounded-md border">
          <div className="grid grid-cols-[1fr_auto_auto_auto] gap-2 bg-muted/60 px-2.5 py-1.5 text-[9.5px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
            <span />
            <span className="text-right">Sim</span>
            <span className="text-right">Real</span>
            <span className="min-w-[52px] text-right">Desvio</span>
          </div>
          {rows.map((r) => (
            <div
              key={r.label}
              className="grid grid-cols-[1fr_auto_auto_auto] items-baseline gap-2 border-t px-2.5 py-1 text-[11.5px]"
            >
              <span className="text-muted-foreground">{r.label}</span>
              <span className="text-right font-mono font-medium tabular-nums">
                {fmt(r.sim, r.unit)}
              </span>
              <span className="text-right font-mono tabular-nums text-muted-foreground">
                {fmt(r.real, r.unit)}
              </span>
              <span className="min-w-[52px] text-right">
                <DeltaValue value={r.delta} unit={r.unit === "pct" ? "pp" : "un"} />
              </span>
            </div>
          ))}
        </div>
      )}
      <p className="mt-1.5 text-[10px] text-muted-foreground">
        Projeção = motor de simulação; Real = registro da execução (OEE via calculate_oee).
      </p>
    </div>
  );
}
